import { Collapse, Box, List, ListItem, BoxProps, HStack, Button } from '@chakra-ui/react'
import { ArticleListItemFragment } from 'web-queries/src/types/generated'
import useTranslation from 'next-translate/useTranslation'
import React, { useState } from 'react'
import { toSimpleDateFormat } from '../utils/date'
import ExternalLink from './ExternalLink'
import HeadingText from './HeadingText'
import { sortBy } from 'lodash-es'
import Link from 'next/link'
import { BiLinkExternal } from 'react-icons/bi'
import MusicianAvatarName from './MusicianAvatarName'
import MusicianPageLinker from './MusicianPageLinker'

interface Props extends BoxProps {
  title?: string
  articles?: ArticleListItemFragment[] | null
  useToggle?: boolean
}

export default function ArticleList({ title, articles = [], useToggle = true, ...props }: Props) {
  const [visibleInfo, setVisibleInfo] = useState(articles?.map(article => article.isRemoved))
  const { t } = useTranslation('article')

  if (!articles || articles?.length === 0) {
    return null
  }

  const sortedArticles = sortBy(articles, 'publishDate').reverse()

  return (
    <Box maxW="1416px" marginTop="16px" {...props}>
      <HeadingText>{title ? title : t('articles')}</HeadingText>
      <List>
        {sortedArticles.map((article: ArticleListItemFragment, i) => (
          <ListItem key={`article-${i}`} padding="8px" bgColor="whiteAlpha.50" marginBottom="16px">
            <HStack
              padding="4px"
              cursor="pointer"
              _hover={{
                backgroundColor: 'blackAlpha.800',
                transition: 'background-color 0.2s ease-in-out',
              }}
              onClick={() => {
                const nextVisibleInfo = [...visibleInfo]
                nextVisibleInfo[i] = !nextVisibleInfo[i]
                setVisibleInfo(nextVisibleInfo)
              }}
            >
              <Box marginRight="4px" w="100px">
                {toSimpleDateFormat(new Date(article.publishDate))}
              </Box>
              <Box>{article.title}</Box>
              <ExternalLink
                href={article.link}
                padding="8px"
                _hover={{
                  backgroundColor: 'blackAlpha.800',
                  transition: 'background-color 0.2s ease-in-out',
                }}
              />
            </HStack>
            <Collapse in={visibleInfo[i]} animateOpacity>
              <Box paddingLeft="92px" marginTop="16px">
                <HStack>
                  {article.lives?.length > 0 && <Box fontWeight="bold">{t('related-live')}</Box>}
                  {article.lives?.map(({ id, title }) => (
                    <Box
                      key={id}
                      marginRight="4px"
                      padding="16px"
                      _hover={{
                        backgroundColor: 'blackAlpha.800',
                        transition: 'background-color 0.2s ease-in-out',
                      }}
                    >
                      <Link href={`/live/${id}`}>{title}</Link>
                    </Box>
                  ))}
                </HStack>
                {article.musicians?.length > 0 && <Box fontWeight="bold">{t('related-musician')}</Box>}
                <Box marginTop="8px" marginBottom="8px" display="flex" flexWrap="wrap">
                  {article.musicians?.map(({ id, useSlug, slug, name, profileImage }) => (
                    <MusicianPageLinker key={`article-musician-${id}`} useSlug={useSlug} slug={slug} id={id}>
                      <Box
                        as="a"
                        padding="8px"
                        _hover={{
                          backgroundColor: 'blackAlpha.800',
                          transition: 'background-color 0.2s ease-in-out',
                        }}
                      >
                        <MusicianAvatarName name={name} profileImage={profileImage} />
                      </Box>
                    </MusicianPageLinker>
                  ))}
                </Box>
                <ExternalLink href={article.link} hideIcon>
                  <Button>
                    <BiLinkExternal size="22px" />
                    <Box display="inline-block" marginLeft="8px">
                      {t('read-more')}
                    </Box>
                  </Button>
                </ExternalLink>
              </Box>
            </Collapse>
          </ListItem>
        ))}
      </List>
    </Box>
  )
}
